import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useTypedSelector } from '@/hooks/use-typed-selector'
import { PropertyKeyModel, PropertyKeyType } from '@/models/property-keys'
import {
    createPropertyKeys,
    fetchReservationPropertyKeys,
    fetchScheduledKeys,
    removePropertyKeys,
} from '@/redux/actions/PropertyKeys'

interface ReturnData {
    readonly displayablePropertyKeys: PropertyKeyModel[];
    readonly isKeysDataLoading: boolean;
    readonly hasDisplayablePropertyKeys: boolean;
    readonly loadReservationPropertyKeys: () => void;
    readonly createReservationPropertyKeys: () => void;
    readonly deleteReservationPropertyKeys: () => void;
    readonly loadScheduledKeys: () => void;
}

export function usePropertyKeys(reservationId: string): ReturnData {
    const dispatch = useDispatch()
    const { propertyKeys, isKeysDataLoading } = useTypedSelector(state => state.propertyKeys)

    const displayablePropertyKeys = useMemo(() => {
        const displayableTypes = [PropertyKeyType.PropertyEntrance, PropertyKeyType.PropertyUnit]
        return propertyKeys.filter((propertyKey) => displayableTypes.includes(propertyKey.type))
    }, [propertyKeys])

    const hasDisplayablePropertyKeys = useMemo(() => {
        return displayablePropertyKeys.length > 0
    }, [displayablePropertyKeys])

    function createReservationPropertyKeys() {
        dispatch(createPropertyKeys(reservationId))
    }

    function deleteReservationPropertyKeys() {
        const propertyKeyIds = propertyKeys.map(propertyKey => propertyKey.id)
        dispatch(removePropertyKeys(propertyKeyIds))
    }

    function loadReservationPropertyKeys() {
        dispatch(fetchReservationPropertyKeys(reservationId))
    }

    function loadScheduledKeys() {
        dispatch(fetchScheduledKeys(reservationId))
    }

    useEffect(() => {
        loadReservationPropertyKeys();
        loadScheduledKeys();
    }, [])

    return {
        displayablePropertyKeys,
        isKeysDataLoading,
        hasDisplayablePropertyKeys,
        loadReservationPropertyKeys,
        createReservationPropertyKeys,
        deleteReservationPropertyKeys,
        loadScheduledKeys,
    }
}
